// src/components/classes/ClassCatalog.jsx
import React, { useState, useEffect } from 'react';
import { 
  BookOpen, 
  Plus, 
  Eye, 
  Edit3, 
  Copy, 
  Trash2, 
  Search,
  Filter,
  Calendar,
  Clock,
  Users,
  MapPin,
  DollarSign,
  Star,
  Tag,
  User,
  X,
  FolderOpen,
  AlertCircle
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useClasses } from './ClassesContext';
import { useEnrollments } from '../enrollments/EnrollmentsProvider';
import classSessionsService from '../../services/classSessionsService';

// Individual Class List Item Component
const ClassListItem = ({ classItem, onView, onEdit, onClone, onDelete, enrollments }) => {
  const [sessions, setSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(true);
  
  const spotsLeft = classItem.maxCapacity - (classItem.currentEnrollment || 0);
  const isNearlyFull = spotsLeft <= 3;
  const isFull = spotsLeft === 0;

  // Calculate total payments for this class
  const calculatePayments = () => {
    if (!enrollments || !Array.isArray(enrollments) || !classItem.id) {
      return { total: 0, paid: 0, pending: 0 };
    }

    const classEnrollments = enrollments.filter(enrollment => 
      enrollment.classId === classItem.id || 
      enrollment.class_id === classItem.id
    );


    let total = 0;
    let paid = 0;
    let pending = 0;

    classEnrollments.forEach(enrollment => {
      const paymentStatus = enrollment.paymentStatus || enrollment.payment_status;
      const totalAmount = parseFloat(enrollment.totalAmount || enrollment.total_amount || classItem.price || 0);
      
      // Calculate amount paid based on payment status
      let amountPaid;
      if (paymentStatus === 'paid') {
        // If marked as paid, use the actual amount_paid if available, otherwise assume full amount
        const recordedAmountPaid = parseFloat(enrollment.amountPaid || enrollment.amount_paid || 0);
        amountPaid = recordedAmountPaid > 0 ? recordedAmountPaid : totalAmount;
      } else {
        // For pending/overdue payments, use the recorded amount paid
        amountPaid = parseFloat(enrollment.amountPaid || enrollment.amount_paid || 0);
      }
      
      paid += amountPaid;
      total += totalAmount;
      pending += (totalAmount - amountPaid);
    });

    return { total, paid, pending };
  };

  const payments = calculatePayments();

  // Load sessions for this class
  useEffect(() => {
    const loadSessions = async () => {
      if (!classItem.id) return;
      
      try {
        setSessionsLoading(true);
        const result = await classSessionsService.getSessionsForClass(classItem.id);
        if (result.success) {
          setSessions(result.data || []);
        }
      } catch (error) {
        console.error('Failed to load sessions for class:', classItem.id, error);
      } finally {
        setSessionsLoading(false);
      }
    };

    loadSessions();
  }, [classItem.id]);

  // Get the main image - only use project image
  const getMainImage = () => {
    // Only use project image if class is linked to a project
    if (classItem.projectId && classItem.projectMainImageUrl) {
      return classItem.projectMainImageUrl;
    }
    // No image available
    return null;
  };

  const mainImage = getMainImage();

  const getDifficultyColor = (difficulty) => {
    switch (difficulty?.toLowerCase()) {
      case 'beginner': return 'bg-[#B7F1B6] text-[#3C3C3C]';
      case 'intermediate': return 'bg-[#FFECC5] text-[#3C3C3C]';
      case 'advanced': return 'bg-[#FFC6C5] text-[#3C3C3C]';
      default: return 'bg-[#D9D9D9] text-[#3C3C3C]';
    }
  };

  const formatTime = (timeString) => {
    if (!timeString) return '';
    const [hours, minutes] = timeString.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'TBD';
    
    // Handle timezone-safe date parsing
    const utcDate = new Date(dateString);
    const year = utcDate.getUTCFullYear();
    const month = utcDate.getUTCMonth();
    const day = utcDate.getUTCDate();
    
    // Create a local date with the same date components
    const localDate = new Date(year, month, day);
    
    return localDate.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Format session information for display
  const formatSessionInfo = () => {
    if (sessionsLoading) {
      return { text: 'Loading schedule...', count: 0 };
    }
    
    if (!sessions || sessions.length === 0) {
      return { text: 'No sessions scheduled', count: 0 };
    }

    // Sort sessions by date using timezone-safe comparison
    const sortedSessions = [...sessions].sort((a, b) => {
      const dateA = new Date(a.session_date);
      const dateB = new Date(b.session_date);
      return dateA.getTime() - dateB.getTime();
    });
    const firstSession = sortedSessions[0];
    const sessionCount = sessions.length;
    
    if (sessionCount === 1) {
      return { 
        text: formatDate(firstSession.session_date),
        count: 1
      };
    } else {
      const firstDate = formatDate(firstSession.session_date);
      return { 
        text: `${sessionCount} sessions, starts ${firstDate}`,
        count: sessionCount
      };
    }
  };

  return (
    <div 
      className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:shadow-md transition-shadow cursor-pointer"
    >
      <div className="flex items-center p-4 gap-4">
        {/* Class Image */}
        <div className="relative w-16 h-16 flex-shrink-0 bg-gray-100 dark:bg-gray-700 rounded-lg overflow-hidden">
          {mainImage ? (
            <img
              src={mainImage}
              alt={classItem.title}
              className="w-full h-full object-cover"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
          ) : null}
          <div className="w-full h-full flex items-center justify-center bg-gray-100 dark:bg-gray-700" style={{ display: mainImage ? 'none' : 'flex' }}>
            <BookOpen className="w-6 h-6 text-gray-400 dark:text-gray-500" />
          </div>
        </div>

        {/* Class Title & Schedule */}
        <div className="flex-1 min-w-0" onClick={() => onView(classItem)}>
          <div className="mb-1">
            <h3 className="text-sm font-medium text-[#3C3C3C] dark:text-white truncate">
              {classItem.title}
            </h3>
          </div>
          <div className="flex items-center text-xs text-gray-600 dark:text-gray-300 mb-1">
            <Calendar className="w-3 h-3 mr-1 flex-shrink-0" />
            <span className="truncate">{formatSessionInfo().text}</span>
            {sessions.length > 0 && sessions[0].start_time && (
              <>
                <Clock className="w-3 h-3 ml-2 mr-1 flex-shrink-0" />
                <span>{formatTime(sessions[0].start_time)}</span>
              </>
            )}
          </div>
          <div className="flex items-center gap-3">
            {classItem.skillLevel && (
              <span className={`text-xs px-2 py-1 rounded-full ${getDifficultyColor(classItem.skillLevel)}`}>
                {classItem.skillLevel}
              </span>
            )}
            {classItem.projectTitle && (
              <>
                {classItem.skillLevel && (
                  <div className="w-px h-4 bg-gray-300 dark:bg-gray-600"></div>
                )}
                <div className="flex items-center gap-1">
                  <FolderOpen className="w-3 h-3 text-gray-500 dark:text-gray-400" />
                  <span className="text-xs text-gray-600 dark:text-gray-300">{classItem.projectTitle}</span>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Instructor */}
        <div className="hidden sm:flex items-center text-xs text-gray-600 dark:text-gray-300 min-w-0 w-32">
          <User className="w-3 h-3 mr-1 flex-shrink-0" />
          <span className="truncate">{classItem.instructor}</span>
        </div>

        {/* Location */}
        {classItem.location && (
          <div className="hidden md:flex items-center text-xs text-gray-600 dark:text-gray-300 min-w-0 w-28">
            <MapPin className="w-3 h-3 mr-1 flex-shrink-0" />
            <span className="truncate">{classItem.location}</span>
          </div>
        )}


        {/* Price */}
        <div className="hidden sm:flex items-center text-xs text-gray-600 dark:text-gray-300 w-16">
          <DollarSign className="w-3 h-3 mr-1 flex-shrink-0" />
          <span>${(parseFloat(classItem.price || 0) + parseFloat(classItem.materialsFee || 0)).toFixed(2)}</span>
        </div>

        {/* Enrollment */}
        <div className="flex items-center w-16">
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
            isFull 
              ? 'bg-red-100 text-red-700' 
              : isNearlyFull 
                ? 'bg-amber-100 text-amber-700' 
                : 'bg-emerald-100 text-emerald-700'
          }`}>
            {classItem.currentEnrollment || 0}/{classItem.maxCapacity}
          </span>
        </div>

        {/* Payments */}
        <div className="hidden md:flex items-center text-xs w-24">
          {payments.total > 0 ? (
            <span className="text-gray-600 dark:text-gray-300">
              <span className="text-green-600 dark:text-green-400 font-medium">
                ${payments.paid.toFixed(0)}
              </span>
              <span className="text-gray-500 dark:text-gray-400 mx-1">of</span>
              <span className="font-medium">
                ${payments.total.toFixed(0)}
              </span>
            </span>
          ) : (
            <span className="text-gray-500 dark:text-gray-400">
              -
            </span>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center space-x-1 w-32">
          <button
            onClick={(e) => { e.stopPropagation(); onView(classItem); }}
            className="bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 p-1.5 rounded transition-colors"
            title="View Details"
          >
            <Eye className="w-3 h-3 text-[#3C3C3C] dark:text-white" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onEdit(classItem); }}
            className="bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 p-1.5 rounded transition-colors"
            title="Edit Class"
          >
            <Edit3 className="w-3 h-3 text-[#3C3C3C] dark:text-white" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onClone(classItem); }}
            className="bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 p-1.5 rounded transition-colors"
            title="Clone Class"
          >
            <Copy className="w-3 h-3 text-[#3C3C3C] dark:text-white" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onDelete(classItem); }}
            className="bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 p-1.5 rounded transition-colors"
            title="Delete Class"
          >
            <Trash2 className="w-3 h-3 text-red-600" />
          </button>
        </div>
      </div>

    </div>
  );
};

// Main ClassCatalog Component
const ClassCatalog = () => {
  const navigate = useNavigate();
  const {
    classes,
    loading,
    error,
    fetchClasses,
    deleteClasses,
    cloneClass,
    clearError,
    clearCache
  } = useClasses();
  const { enrollments } = useEnrollments();

  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [sortBy, setSortBy] = useState(() => {
    return localStorage.getItem('classCatalog-sortBy') || 'title';
  });
  const [sortOrder, setSortOrder] = useState(() => {
    return localStorage.getItem('classCatalog-sortOrder') || 'asc';
  });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [classToDelete, setClassToDelete] = useState(null);
  const [filters, setFilters] = useState({
    category: '',
    skillLevel: '',
    instructor: '',
    status: 'active',
    startDateFrom: '',
    startDateTo: ''
  });
  const [hidePastClasses, setHidePastClasses] = useState(() => {
    return localStorage.getItem('classCatalog-hidePastClasses') === 'true';
  });
  const [classSessions, setClassSessions] = useState({});

  // Load classes on mount
  useEffect(() => {
    fetchClasses();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load sessions for all classes for sorting
  useEffect(() => {
    const loadAllSessions = async () => {
      if (!classes || classes.length === 0) return;

      const sessionsMap = {};
      await Promise.all(
        classes.map(async (classItem) => {
          try {
            const result = await classSessionsService.getSessionsForClass(classItem.id);
            if (result.success && result.data && result.data.length > 0) {
              // Sort sessions by date and get the first one
              const sorted = [...result.data].sort((a, b) => {
                return new Date(a.session_date).getTime() - new Date(b.session_date).getTime();
              });
              sessionsMap[classItem.id] = sorted[0].session_date;
            }
          } catch (error) {
            console.error('Failed to load sessions for class:', classItem.id, error);
          }
        })
      );
      setClassSessions(sessionsMap);
    };

    loadAllSessions();
  }, [classes]);

  // Persist sort preferences
  useEffect(() => {
    localStorage.setItem('classCatalog-sortBy', sortBy);
  }, [sortBy]);

  useEffect(() => {
    localStorage.setItem('classCatalog-sortOrder', sortOrder);
  }, [sortOrder]);

  useEffect(() => {
    localStorage.setItem('classCatalog-hidePastClasses', hidePastClasses.toString());
  }, [hidePastClasses]);

  // Filter classes based on search and filters
  const filteredClasses = classes.filter(classItem => {
    const matchesSearch = !searchTerm ||
      classItem.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      classItem.instructor.toLowerCase().includes(searchTerm.toLowerCase()) ||
      classItem.category.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesCategory = !filters.category || classItem.category === filters.category;
    const matchesSkillLevel = !filters.skillLevel || classItem.skillLevel === filters.skillLevel;
    const matchesInstructor = !filters.instructor || classItem.instructor === filters.instructor;

    // Date filtering
    const classFirstSessionDate = classSessions[classItem.id] || classItem.startDate;
    let matchesStartDateFrom = true;
    let matchesStartDateTo = true;

    if (filters.startDateFrom && classFirstSessionDate) {
      const filterDate = new Date(filters.startDateFrom);
      const classDate = new Date(classFirstSessionDate);
      matchesStartDateFrom = classDate >= filterDate;
    }

    if (filters.startDateTo && classFirstSessionDate) {
      const filterDate = new Date(filters.startDateTo);
      const classDate = new Date(classFirstSessionDate);
      matchesStartDateTo = classDate <= filterDate;
    }

    // Hide past classes filter
    let matchesPastFilter = true;
    if (hidePastClasses && classFirstSessionDate) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const classDate = new Date(classFirstSessionDate);
      matchesPastFilter = classDate >= today;
    }

    return matchesSearch && matchesCategory && matchesSkillLevel && matchesInstructor &&
           matchesStartDateFrom && matchesStartDateTo && matchesPastFilter;
  });

  // Sort classes
  const sortedClasses = [...filteredClasses].sort((a, b) => {
    let aValue, bValue;

    switch (sortBy) {
      case 'title':
        aValue = a.title.toLowerCase();
        bValue = b.title.toLowerCase();
        break;
      case 'instructor':
        aValue = a.instructor.toLowerCase();
        bValue = b.instructor.toLowerCase();
        break;
      case 'category':
        aValue = a.category.toLowerCase();
        bValue = b.category.toLowerCase();
        break;
      case 'skillLevel':
        const skillOrder = { 'Beginner': 1, 'Intermediate': 2, 'Advanced': 3 };
        aValue = skillOrder[a.skillLevel] || 0;
        bValue = skillOrder[b.skillLevel] || 0;
        break;
      case 'startDate':
        // Use first session date from loaded sessions, fallback to class startDate
        aValue = classSessions[a.id] ? new Date(classSessions[a.id]).getTime() : (a.startDate ? new Date(a.startDate).getTime() : 0);
        bValue = classSessions[b.id] ? new Date(classSessions[b.id]).getTime() : (b.startDate ? new Date(b.startDate).getTime() : 0);
        break;
      case 'price':
        aValue = parseFloat(a.price) || 0;
        bValue = parseFloat(b.price) || 0;
        break;
      case 'capacity':
        aValue = a.maxCapacity || 0;
        bValue = b.maxCapacity || 0;
        break;
      default:
        aValue = a.title.toLowerCase();
        bValue = b.title.toLowerCase();
    }

    if (sortOrder === 'asc') {
      return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
    } else {
      return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
    }
  });

  // Get unique values for filter dropdowns
  const getUniqueValues = (field) => {
    const values = classes.map(cls => cls[field]).filter(Boolean);
    return [...new Set(values)].sort();
  };

  // Action handlers
  const handleView = (classItem) => {
    navigate(`/classes/view/${classItem.id}`);
  };

  const handleEdit = (classItem) => {
    console.log('ClassCatalog - handleEdit called with classItem:', classItem);
    console.log('ClassCatalog - classItem.id:', classItem.id);
    console.log('ClassCatalog - classItem.id type:', typeof classItem.id);
    
    // Validate that we have a valid ID before navigating
    if (!classItem.id || classItem.id === 'undefined' || classItem.id === 'null') {
      console.error('ClassCatalog - Invalid class ID, cannot edit:', classItem.id);
      // Show error in the UI instead of alert
      console.error('Error: Cannot edit class - invalid ID. Please refresh the page and try again.');
      return;
    }
    
    navigate(`/classes/edit/${classItem.id}`);
  };

  const handleClone = async (classItem) => {
    try {
      await cloneClass(classItem.id);
      // Optionally show a success message
      console.log(`Successfully cloned class: ${classItem.title}`);
    } catch (error) {
      console.error('Failed to clone class:', error);
      // Optionally show an error message to the user
    }
  };

  const handleDelete = (classItem) => {
    setClassToDelete(classItem);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (classToDelete) {
      try {
        await deleteClasses([classToDelete.id]);
      } catch (error) {
        console.error('Failed to delete class:', error);
      }
    }
    setShowDeleteModal(false);
    setClassToDelete(null);
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSortBy('title');
    setSortOrder('asc');
    setHidePastClasses(false);
    setFilters({
      category: '',
      skillLevel: '',
      instructor: '',
      status: 'active',
      startDateFrom: '',
      startDateTo: ''
    });
    // Clear persisted preferences when clearing filters
    localStorage.removeItem('classCatalog-sortBy');
    localStorage.removeItem('classCatalog-sortOrder');
    localStorage.removeItem('classCatalog-hidePastClasses');
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <BookOpen className="w-8 h-8 text-[#7A99D8]" />
            <div>
              <h1 className="text-lg font-bold text-[#3C3C3C] dark:text-white">Classes</h1>
              <p className="text-sm text-gray-600 dark:text-gray-300">{sortedClasses.length} classes • Manage your classes and schedules</p>
            </div>
          </div>
          <Link
            to="/classes/add"
            className="bg-[#FFECC5] hover:bg-[#FFE4A3] text-[#3C3C3C] px-4 py-2 rounded-lg font-medium flex items-center space-x-2 transition-colors"
          >
            <Plus className="w-4 h-4" />
            <span>Add Class</span>
          </Link>
        </div>
      </div>

      <div className="px-6 py-6">{/* Content wrapper */}

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6 flex items-center gap-3">
            <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center">
              <X className="w-4 h-4 text-red-600" />
            </div>
            <div className="flex-1">
              <h3 className="text-sm font-medium text-red-800">Error loading classes</h3>
              <p className="text-sm text-red-700 mt-1">{error}</p>
            </div>
            <button
              onClick={clearError}
              className="text-red-600 hover:text-red-800 p-1"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Search and Filters */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 mb-6">
          <div className="space-y-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500 w-4 h-4" />
              <input
                type="text"
                placeholder="Search classes by title, instructor, or category..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#7A99D8] focus:border-transparent"
              />
            </div>


            {/* Filter Toggle */}
            <div className="flex items-center justify-between">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center space-x-2 text-sm text-[#3C3C3C] dark:text-white hover:text-[#7A99D8] transition-colors"
              >
                <Filter className="w-4 h-4" />
                <span>Filters</span>
              </button>

              <button
                onClick={clearFilters}
                className="text-sm text-gray-600 dark:text-gray-300 hover:text-[#3C3C3C] dark:hover:text-gray-100 transition-colors"
              >
                Clear all filters
              </button>
            </div>
          </div>

          {/* Expandable Filters */}
          {showFilters && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-gray-200 dark:border-gray-700">
              <div>
                <label className="block text-sm font-medium text-[#3C3C3C] dark:text-white mb-2">Category</label>
                <select
                  value={filters.category}
                  onChange={(e) => setFilters(prev => ({ ...prev, category: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-200 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#7A99D8] focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="">All Categories</option>
                  {getUniqueValues('category').map(category => (
                    <option key={category} value={category}>{category}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#3C3C3C] dark:text-white mb-2">Skill Level</label>
                <select
                  value={filters.skillLevel}
                  onChange={(e) => setFilters(prev => ({ ...prev, skillLevel: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-200 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#7A99D8] focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="">All Levels</option>
                  <option value="Beginner">Beginner</option>
                  <option value="Intermediate">Intermediate</option>
                  <option value="Advanced">Advanced</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#3C3C3C] dark:text-white mb-2">Start Date From</label>
                <input
                  type="date"
                  value={filters.startDateFrom}
                  onChange={(e) => setFilters(prev => ({ ...prev, startDateFrom: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-200 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#7A99D8] focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#3C3C3C] dark:text-white mb-2">Start Date To</label>
                <input
                  type="date"
                  value={filters.startDateTo}
                  onChange={(e) => setFilters(prev => ({ ...prev, startDateTo: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-200 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#7A99D8] focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
            </div>
          )}
        </div>

        {/* Results Summary and Sorting */}
        <div className="flex items-center justify-between mb-6">
          <span className="text-gray-600 dark:text-gray-300">
            Showing {sortedClasses.length} of {classes.length} classes
          </span>
          <div className="flex items-center gap-4">
            {/* Hide Past Classes Checkbox */}
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={hidePastClasses}
                onChange={(e) => setHidePastClasses(e.target.checked)}
                className="w-4 h-4 text-[#7A99D8] border-gray-300 dark:border-gray-600 rounded focus:ring-[#7A99D8] focus:ring-2"
              />
              <span className="text-sm font-medium text-[#3C3C3C] dark:text-white">Hide Classes in the Past</span>
            </label>

            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-[#3C3C3C] dark:text-white">Sort by:</label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="px-3 py-1 border border-gray-200 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#7A99D8] focus:border-transparent text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="title">Title</option>
                <option value="instructor">Instructor</option>
                <option value="category">Category</option>
                <option value="skillLevel">Skill Level</option>
                <option value="startDate">Start Date</option>
                <option value="price">Price</option>
                <option value="capacity">Capacity</option>
              </select>
            </div>
            <button
              onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
              className="flex items-center gap-1 text-sm text-[#3C3C3C] dark:text-white hover:text-[#7A99D8] transition-colors"
            >
              {sortOrder === 'asc' ? '↑' : '↓'} {sortOrder === 'asc' ? 'A-Z' : 'Z-A'}
            </button>
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center py-16">
            <div className="text-center">
              <div className="w-8 h-8 border-4 border-emerald-200 border-t-emerald-600 rounded-full animate-spin mx-auto mb-4"></div>
              <span className="text-gray-600 dark:text-gray-300">Loading classes...</span>
            </div>
          </div>
        )}

        {/* Classes List */}
        {!loading && sortedClasses.length > 0 && (
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
            {/* Table Header */}
            <div className="bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-700 px-4 py-3">
              <div className="flex items-center gap-4 text-xs font-medium text-gray-600 dark:text-gray-300 uppercase tracking-wide">
                <div className="flex-1">Class & Schedule</div>
                <div className="hidden sm:block w-32">Instructor</div>
                <div className="hidden md:block w-28">Location</div>
                <div className="hidden sm:block w-16">Price</div>
                <div className="w-16">Enrolled</div>
                <div className="hidden md:block w-24">Payments</div>
                <div className="w-32">Actions</div>
              </div>
            </div>
            
            {/* Classes List */}
            <div className="divide-y divide-gray-200 dark:divide-gray-700">
              {sortedClasses.map(classItem => (
                <ClassListItem
                  key={classItem.id}
                  classItem={classItem}
                  enrollments={enrollments}
                  onView={handleView}
                  onEdit={handleEdit}
                  onClone={handleClone}
                  onDelete={handleDelete}
                />
              ))}
            </div>
          </div>
        )}

        {/* Empty State */}
        {!loading && sortedClasses.length === 0 && (
          <div className="text-center py-16">
            <div className="w-24 h-24 mx-auto mb-6 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center">
              <BookOpen className="w-12 h-12 text-gray-400 dark:text-gray-500" />
            </div>
            <h3 className="text-xl font-semibold text-[#3C3C3C] dark:text-white mb-3">
              {classes.length === 0 ? 'No classes yet' : 'No classes found'}
            </h3>
            <p className="text-gray-600 dark:text-gray-300 mb-8 max-w-md mx-auto">
              {classes.length === 0 
                ? "Get started by creating your first class to manage student enrollments and schedules."
                : "Try adjusting your search or filters to find classes."
              }
            </p>
            <Link
              to="/classes/add"
              className="bg-[#FFECC5] hover:bg-[#FFE4A3] text-[#3C3C3C] px-6 py-3 rounded-lg inline-flex items-center gap-2 transition-colors font-medium"
            >
              <Plus className="w-5 h-5" />
              {classes.length === 0 ? 'Create Your First Class' : 'Add New Class'}
            </Link>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteModal && classToDelete && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg max-w-md w-full mx-4">
              <div className="p-6">
                <div className="flex items-center mb-4">
                  <AlertCircle className="w-6 h-6 text-red-500 mr-3" />
                  <h3 className="text-lg font-semibold text-[#3C3C3C] dark:text-white">Delete Class</h3>
                </div>
                
                <p className="text-gray-600 dark:text-gray-300 mb-6">
                  Are you sure you want to delete "{classToDelete.title}"? This action cannot be undone.
                </p>
                
                <div className="flex justify-end space-x-3">
                  <button
                    onClick={() => {
                      setShowDeleteModal(false);
                      setClassToDelete(null);
                    }}
                    className="px-4 py-2 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-900 dark:text-white rounded-lg font-medium transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={confirmDelete}
                    className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors"
                  >
                    Delete Class
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ClassCatalog;